import React from 'react';

import {useGlobalAudioPlayer} from 'react-use-audio-player'


import {Subject} from 'rxjs';

import springboard from 'springboard';
import {FileInfo} from 'springboard/modules/files/file_types';

import {AudioPlayer} from '@/components/audio_player';
import {StoredFileMeta} from '../files/files_module';
import {DBObjects} from '@/store/store_types';
import {useSubject} from 'springboard/module_registry/module_registry';
import {StateSupervisor} from 'springboard/services/states/shared_state_service';
import {useModule} from '@/hooks/useModule';
import {IconPlayerPause, IconPlayerPlay} from '@tabler/icons-react';
import {isAudioFile} from '@/utils/file_utils';

declare module 'springboard/module_registry/module_registry' {
    interface AllModules {
        AudioPlayer: AudioPlayerModule;
    }
}

type GlobalAudioPlayerProps = {
}

type AudioPlayerModule = {
    playFile: (fileInfo: DBObjects['File']) => void;
    playPauseToggle: () => void;
    playAndSeek: (fileInfo: DBObjects['File'], seconds: number) => void;
    seekToSeconds: (seconds: number) => void;
    useAudioPlaybackStatus: () => AudioPlaybackState | null;
    GlobalAudioPlayer: React.ElementType<GlobalAudioPlayerProps>;
    currentlyPlayingFile: StateSupervisor<DBObjects['File'] | null>;
    mostRecentAutomatedSeek: Subject<{seekTime: number} | null>;
    Provider: (props: React.PropsWithChildren) => React.ReactNode;
}

type AudioPlaybackState = {
    fileInfo: DBObjects['File'];
    isPlaying: boolean;
};

springboard.registerModule('AudioPlayer', {}, async (moduleAPI): Promise<AudioPlayerModule> => {
    const currentlyPlayingFile = await moduleAPI.statesAPI.createUserAgentState<DBObjects['File'] | null>('currentlyPlayingFile', null);
    currentlyPlayingFile.setState(null);

    const mostRecentAutomatedSeek = new Subject<{seekTime: number} | null>();

    let isPlaying = false;
    const isPlayingSubject = new Subject<boolean>();

    // const shouldPlay = await moduleAPI.statesAPI.createLocalState('shouldPlay', false);

    const playFile = (fileInfo: DBObjects['File']) => {
        if (fileInfo.mime_type?.startsWith('audio') || isAudioFile(fileInfo.name)) {
            currentlyPlayingFile.setState(fileInfo);
            setTimeout(() => {
                const action = moduleAPI.getModule('Kysely').getActionForWorkspace(fileInfo.workspace_id, 'createUserActivity');

                action({
                    workspace_id: fileInfo.workspace_id,
                    activity_name: 'LISTEN',
                    object_type: 'File',
                    object_id: fileInfo.id,
                }, {workspace_id: fileInfo.workspace_id});
            });
        }
        // load?.(`${FILE_SERVER_HOST}/${fileInfo.path}`, {
        //     html5: true,
        // });
    };

    const playPauseToggle = () => {
        // shouldPlay.setState(should => !should);
    };

    let load: ReturnType<typeof useGlobalAudioPlayer>['load'] | null = null;

    const seekToSeconds = (seconds: number) => {
        mostRecentAutomatedSeek.next({seekTime: seconds});
    };

    const playAndSeek = (fileInfo: DBObjects['File'], seconds: number) => {
        if (fileInfo.mime_type?.startsWith('audio')) {
            if (currentlyPlayingFile.getState()?.id === fileInfo.id) {
                seekToSeconds(seconds);
            } else {
                playFile(fileInfo);

                setTimeout(() => {
                    seekToSeconds(seconds);
                }, 100);
            }
        }
    };

    return {
        playFile,
        playPauseToggle,
        playAndSeek,
        currentlyPlayingFile,
        mostRecentAutomatedSeek,
        seekToSeconds,
        useAudioPlaybackStatus: (): AudioPlaybackState | null => {
            const currentFile = currentlyPlayingFile.useState();
            const isPlayingHooked = useSubject(isPlaying, isPlayingSubject);

            if (!currentFile) {
                return null;
            }

            return {
                fileInfo: currentFile,
                isPlaying: isPlayingHooked,
            };
        },
        Provider: (props: React.PropsWithChildren) => {
            const playbackStatus = moduleAPI.getModule('AudioPlayer').useAudioPlaybackStatus();

            React.useEffect(() => {
                const DEFAULT_TITLE = 'SongDrive';

                if (playbackStatus && playbackStatus.isPlaying && playbackStatus.fileInfo) {
                    // Extract song name without file extension
                    const fileName = playbackStatus.fileInfo.name;
                    const songName = fileName.replace(/\.[^/.]+$/, ''); // Remove file extension

                    // Update the page title with playing indicator
                    document.title = `${songName}`;
                } else if (playbackStatus && !playbackStatus.isPlaying && playbackStatus.fileInfo) {
                    // Show paused state
                    const fileName = playbackStatus.fileInfo.name;
                    const songName = fileName.replace(/\.[^/.]+$/, '');

                    document.title = `${songName}`;
                } else {
                    // Reset to default title when no song is loaded
                    document.title = DEFAULT_TITLE;
                }
            }, [playbackStatus]);

            return props.children;
        },
        GlobalAudioPlayer: (props: GlobalAudioPlayerProps) => {
            const seekValue = useSubject(null, mostRecentAutomatedSeek);
            const globalAudioPlayer = useGlobalAudioPlayer();
            load = globalAudioPlayer.load;

            const fileClient = useModule('MSFiles').client();

            return (
                <AudioPlayer
                    fileInfo={currentlyPlayingFile.useState()}
                    getFileSrc={fileClient.getFileSrc}
                    bottomNav={true}
                    onPlaying={() => {
                        isPlaying = true;
                        isPlayingSubject.next(true);
                    }}
                    onPause={() => {
                        isPlaying = false;
                        isPlayingSubject.next(false);
                    }}
                // mostRecentAutomatedSeek={seekValue}
                />
            );
        },
    };
});

export const PlayIcon = (props: {fileInfo: DBObjects['File']; style?: React.CSSProperties; size?: number}) => {
    const audioPlayerModule = useModule('AudioPlayer');
    const playbackStatus = audioPlayerModule.useAudioPlaybackStatus();
    const currentPlayingFile = audioPlayerModule.currentlyPlayingFile.useState();

    const isPlaying = playbackStatus?.isPlaying;
    const isCurrentFile = currentPlayingFile?.id === props.fileInfo.id;

    return (
        <>
            {isPlaying && isCurrentFile ? (
                <button
                    className='play-icon'
                    aria-label="Pause audio"
                    tabIndex={0}
                    style={{
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: '8px',
                        margin: '-8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        ...(props.style || {}),
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        (window as any).togglePlayPause();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            (window as any).togglePlayPause();
                        }
                    }}
                >
                    <IconPlayerPause aria-hidden="true" size={props.size} />
                </button>
            ) : (
                <button
                    className='play-icon'
                    aria-label={isCurrentFile ? "Resume audio" : "Play audio"}
                    tabIndex={0}
                    style={{
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        padding: '8px',
                        margin: '-8px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        ...(props.style || {}),
                    }}
                    onClick={(e) => {
                        e.stopPropagation();

                        if (isCurrentFile) {
                            (window as any).togglePlayPause();
                        } else {
                            audioPlayerModule.playFile(props.fileInfo);
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();

                            if (isCurrentFile) {
                                (window as any).togglePlayPause();
                            } else {
                                audioPlayerModule.playFile(props.fileInfo);
                            }
                        }
                    }}
                >
                    <IconPlayerPlay aria-hidden="true" size={props.size} />
                </button>
            )}
        </>
    );
};
